/**
 * Loading for the row-level artefacts behind Claim detail and Touch detail.
 *
 * They are tens of MB, so they are kept in IndexedDB and shown from there straight away
 * on a return visit, then revalidated against the server's ETag in the background:
 * a 304 costs a few hundred bytes and no server work; a 200 (the morning refresh landed,
 * or the caller's access changed) replaces what is on screen.
 *
 * Cached per signed-in account. The ETag already encodes the caller, so a stale entry
 * could never survive revalidation — but showing it BEFORE revalidating would flash the
 * previous user's rows on a shared machine. Sign-out clears the store as well.
 */
import { useEffect, useState } from 'react'
import { useAuth } from '@/auth/AuthProvider'
import { TOKEN_HEADER } from './client'
import { apiFetch } from '@/auth/apiFetch'
import * as cache from './cache'
import { viewAsFailure, viewAsKey, withViewAs } from './viewAs'

interface State<T> {
  data: T | null
  error: string | null
}

type Listener<T> = (s: State<T>) => void

interface Slot<T> {
  key: string
  state: State<T>
  listeners: Set<Listener<T>>
  started: boolean
}

// One slot per artefact, shared by every component that reads it, so opening the page
// twice (or two components on it) never starts two downloads.
const slots = new Map<string, Slot<unknown>>()

function emit<T>(slot: Slot<T>, next: Partial<State<T>>) {
  slot.state = { ...slot.state, ...next }
  for (const l of slot.listeners) l(slot.state)
}

async function run<T>(slot: Slot<T>, url: string, token: string | null, failure: string) {
  const cached = await cache.readDetail<T>(slot.key)
  if (cached && slot.state.data === null) emit(slot, { data: cached.data })

  const headers = withViewAs({ Accept: 'application/json' })
  if (token) headers[TOKEN_HEADER] = token
  if (cached?.etag) headers['If-None-Match'] = cached.etag

  try {
    const res = await apiFetch(url, { headers })
    if (res.status === 304 && cached) return
    if (!res.ok) throw new Error((await viewAsFailure(res)) ?? failure)
    const data = (await res.json()) as T
    emit(slot, { data, error: null })
    await cache.writeDetail(slot.key, res.headers.get('ETag') ?? '', data)
  } catch (e) {
    // With rows already on screen, a failed revalidation is not worth an error: the
    // cached copy is still this user's, and the next visit tries again.
    if (slot.state.data === null) emit(slot, { error: e instanceof Error ? e.message : String(e) })
    slot.started = false
  }
}

export function useCachedDetail<T>(
  name: string,
  url: string,
  failure: string,
  /** False holds the download back until a page actually needs the rows. */
  enabled = true,
): State<T> {
  const { token, ready, account } = useAuth()
  // Per account, and per view-as target: one person's rows are never shown in another's view.
  const key = viewAsKey(`${name}|${account?.homeAccountId ?? 'local'}`)
  const [state, setState] = useState<State<T>>(
    () => ((slots.get(key) as Slot<T> | undefined)?.state ?? { data: null, error: null }),
  )

  useEffect(() => {
    if (!ready || !enabled) return
    let slot = slots.get(key) as Slot<T> | undefined
    if (!slot) {
      slot = { key, state: { data: null, error: null }, listeners: new Set(), started: false }
      slots.set(key, slot as Slot<unknown>)
    }
    const s = slot
    s.listeners.add(setState)
    setState(s.state)
    if (!s.started) {
      s.started = true
      void run(s, url, token, failure)
    }
    return () => {
      s.listeners.delete(setState)
    }
  }, [key, url, token, ready, failure, enabled])

  return state
}

/** Forget the in-memory copies too — sign-out must not leave rows in a live tab. */
export function forgetDetail(): void {
  slots.clear()
}
